import { MfaMethod } from '@/generated/graphql';

/** dispatch-api's code for "verify your second factor again". */
export const STEP_UP_REQUIRED_CODE = 'auth:mfa_step_up_required';

/** The step-up's own operations never open the prompt. */
export const STEP_UP_OPERATIONS = [
  'StartMfaStepUp',
  'VerifyMfaStepUp',
  'MfaAuthenticationOptions',
];

/** What auth:mfa_step_up_required says about the step-up it wants. */
export interface StepUpRequest {
  /** The user's methods. Empty when the user has no factor to verify. */
  methods?: MfaMethod[];
  /** Seconds the verification counts for. */
  maxAge?: number;
}

type StepUpHandler = (request: StepUpRequest) => Promise<boolean>;

let handler: StepUpHandler | null = null;
let pending: Promise<boolean> | null = null;

/** StepUpPrompt registers itself here while it is mounted. */
export function setStepUpHandler(next: StepUpHandler | null): void {
  handler = next;
}

/**
 * Opens the step-up prompt and resolves true once the user verified, or
 * false when they cancelled or no prompt is mounted (server rendering).
 * Requests that fail together share one prompt.
 */
export function requestStepUp(request: StepUpRequest): Promise<boolean> {
  if (!handler) {
    return Promise.resolve(false);
  }

  if (!pending) {
    pending = handler(request).finally(() => {
      pending = null;
    });
  }

  return pending;
}

/** Reads `methods` and `maxAge` from the error's extensions. */
export function stepUpRequestFrom(
  extensions?: Record<string, unknown> | null,
): StepUpRequest {
  const { methods, maxAge } = extensions ?? {};

  return {
    methods: Array.isArray(methods) ? (methods as MfaMethod[]) : undefined,
    maxAge: typeof maxAge === 'number' ? maxAge : undefined,
  };
}
